import { HtmlTags } from "@components/common/html-tags";
import { Icon, Spinner } from "@components/common/icon";
import { PaginatorCursor } from "@components/common/paginator-cursor";
import { PictrsImage } from "@components/common/pictrs-image";
import { CreateCommunityButton } from "@components/common/content-actions/create-item-buttons";
import { FirstLoadService } from "@services/FirstLoadService";
import {
  EMPTY_REQUEST,
  HttpService,
  LOADING_REQUEST,
  RequestState,
  wrapClient,
} from "@services/HttpService";
import { editCommunity, setIsoData } from "@utils/app";
import { isBrowser } from "@utils/browser";
import { communityLimit } from "@utils/config";
import { getHttpBaseInternal } from "@utils/env";
import { aimooT } from "@utils/aimoo-i18n";
import {
  getQueryParams,
  getQueryString,
  numToSI,
  resourcesSettled,
} from "@utils/helpers";
import { IRoutePropsWithFetch } from "@utils/routes";
import {
  InitialFetchRequest,
  QueryParams,
  RouteDataResponse,
} from "@utils/types";
import {
  CommunityCategory,
  browserListCommunityCategories,
  listCommunityCategories,
  ListCommunityCategoriesResponse,
} from "@utils/admin-api";
import { Component, FormEvent, InfernoNode, linkEvent } from "inferno";
import { Link, RouteComponentProps, RouterContext } from "inferno-router";
import {
  CommunityResponse,
  CommunitySortType,
  CommunityView,
  LemmyHttp,
  ListCommunities,
  ListingType,
  MyUserInfo,
  PagedResponse,
  PaginationCursor,
} from "lemmy-js-client";
import { communityLink } from "../community/community-link";
import { scrollMixin } from "../mixins/scroll-mixin";

type ListCommunitiesWithCategory = ListCommunities & {
  category_id?: number;
};

type CommunityHomeData = RouteDataResponse<{
  listCommunitiesResponse: PagedResponse<CommunityView>;
  trendingCommunitiesResponse: PagedResponse<CommunityView>;
  listCategoriesResponse: ListCommunityCategoriesResponse;
}>;

interface CommunityHomeState {
  listCommunitiesResponse: RequestState<PagedResponse<CommunityView>>;
  trendingCommunitiesResponse: RequestState<PagedResponse<CommunityView>>;
  listCategoriesResponse: RequestState<ListCommunityCategoriesResponse>;
  searchText: string;
  followLoadingId?: number;
  isIsomorphic: boolean;
}

interface CommunityHomeProps {
  listingType: ListingType;
  sort: CommunitySortType;
  cursor?: PaginationCursor;
  search_term?: string;
  category_id?: number;
}

type CommunityHomePathProps = Record<string, never>;
type CommunityHomeRouteProps = RouteComponentProps<CommunityHomePathProps> &
  CommunityHomeProps;

export type CommunityHomeFetchConfig = IRoutePropsWithFetch<
  CommunityHomeData,
  CommunityHomePathProps,
  CommunityHomeProps
>;

function getListingTypeFromQuery(listingType?: string): ListingType {
  return listingType ? (listingType as ListingType) : "local";
}

function getSortTypeFromQuery(type?: string): CommunitySortType {
  return type ? (type as CommunitySortType) : "hot";
}

function getCategoryIdFromQuery(categoryId?: string): number | undefined {
  if (!categoryId) {
    return undefined;
  }
  const parsed = Number(categoryId);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

export function getCommunityHomeQueryParams(
  source?: string,
): CommunityHomeProps {
  return getQueryParams<CommunityHomeProps>(
    {
      listingType: getListingTypeFromQuery,
      sort: getSortTypeFromQuery,
      cursor: (cursor?: string) => cursor,
      search_term: (searchTerm?: string) => searchTerm,
      category_id: getCategoryIdFromQuery,
    },
    source,
  );
}

const AVATAR_GRADIENTS = [
  "aimoo-avatar-1",
  "aimoo-avatar-2",
  "aimoo-avatar-3",
  "aimoo-avatar-4",
  "aimoo-avatar-5",
  "aimoo-avatar-6",
];

function avatarGradientClass(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash + name.charCodeAt(i) * (i + 1)) % AVATAR_GRADIENTS.length;
  }
  return AVATAR_GRADIENTS[hash] ?? AVATAR_GRADIENTS[0];
}

@scrollMixin
export class CommunityHome extends Component<
  CommunityHomeRouteProps,
  CommunityHomeState
> {
  private isoData = setIsoData<CommunityHomeData>(this.context);
  state: CommunityHomeState = {
    listCommunitiesResponse: EMPTY_REQUEST,
    trendingCommunitiesResponse: EMPTY_REQUEST,
    listCategoriesResponse: EMPTY_REQUEST,
    searchText: "",
    isIsomorphic: false,
  };

  loadingSettled() {
    return resourcesSettled([
      this.state.listCommunitiesResponse,
      this.state.trendingCommunitiesResponse,
      this.state.listCategoriesResponse,
    ]);
  }

  constructor(props: CommunityHomeRouteProps, context: object) {
    super(props, context);

    if (FirstLoadService.isFirstLoad) {
      const {
        listCommunitiesResponse,
        trendingCommunitiesResponse,
        listCategoriesResponse,
      } = this.isoData.routeData;
      this.state = {
        ...this.state,
        listCommunitiesResponse,
        trendingCommunitiesResponse,
        listCategoriesResponse,
        searchText: props.search_term ?? "",
        isIsomorphic: true,
      };
    } else {
      this.state = {
        ...this.state,
        searchText: props.search_term ?? "",
      };
    }
  }

  async componentWillMount() {
    if (!this.state.isIsomorphic && isBrowser()) {
      await this.refetch(this.props);
    }
  }

  async componentWillReceiveProps(nextProps: CommunityHomeRouteProps) {
    this.setState({ searchText: nextProps.search_term ?? "" });
    await this.refetch(nextProps);
  }

  get documentTitle(): string {
    return `${aimooT("community")} - ${this.isoData.siteRes?.site_view.site.name}`;
  }

  get categories(): CommunityCategory[] {
    return this.state.listCategoriesResponse.state === "success"
      ? this.state.listCategoriesResponse.data.categories
      : [];
  }

  render() {
    const localSite = this.isoData.siteRes.site_view.local_site;
    const myUserInfo = this.isoData.myUserInfo;
    const activeCategoryId = this.props.category_id;

    return (
      <div className="aimoo-community-home">
        <HtmlTags
          title={this.documentTitle}
          context={this.context as RouterContext}
        />

        <div className="aimoo-community-home__inner container-lg px-3">
          <header className="aimoo-community-home__header">
            <div>
              <h1 className="aimoo-community-home__title">
                {aimooT("community")}
              </h1>
              <p className="aimoo-community-home__subtitle">
                {aimooT("home_subtitle")}
              </p>
            </div>
            <CreateCommunityButton
              localSite={localSite}
              myUserInfo={myUserInfo}
              blockButton={false}
              label={aimooT("create_community_btn")}
            />
          </header>

          <div className="aimoo-community-home__toolbar">
            <form
              className="aimoo-community-home__search"
              onSubmit={linkEvent(this, handleSearchSubmit)}
            >
              <Icon icon="search" classes="aimoo-community-home__search-icon" />
              <input
                type="search"
                className="form-control"
                placeholder={aimooT("search_community")}
                value={this.state.searchText}
                onInput={linkEvent(this, handleSearchInput)}
              />
            </form>
            <div className="aimoo-community-home__chips">
              <button
                type="button"
                className={`aimoo-chip${!activeCategoryId ? " aimoo-chip--active" : ""}`}
                onClick={() =>
                  this.updateUrl({
                    category_id: undefined,
                    cursor: undefined,
                  })
                }
              >
                {aimooT("all")}
              </button>
              {this.categories.map(category => (
                <button
                  key={category.id}
                  type="button"
                  className={`aimoo-chip${
                    activeCategoryId === category.id
                      ? " aimoo-chip--active"
                      : ""
                  }`}
                  onClick={() =>
                    this.updateUrl({
                      category_id: category.id,
                      cursor: undefined,
                    })
                  }
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          <div className="aimoo-community-home__layout">
            <div className="aimoo-community-home__main">
              {this.renderCommunityCards()}
              <PaginatorCursor
                current={this.props.cursor}
                resource={this.state.listCommunitiesResponse}
                onPageChange={cursor => this.updateUrl({ cursor })}
              />
            </div>
            <aside className="aimoo-community-home__sidebar">
              {this.renderTrendingSidebar()}
              {this.renderMyCommunitiesSidebar(myUserInfo)}
            </aside>
          </div>
        </div>
      </div>
    );
  }

  renderCommunityCards(): InfernoNode {
    switch (this.state.listCommunitiesResponse.state) {
      case "loading":
        return (
          <div className="text-center py-5">
            <Spinner large />
          </div>
        );
      case "failed":
        return (
          <div className="alert alert-danger">
            {aimooT("load_communities_failed")}
          </div>
        );
      case "success": {
        const communities = this.state.listCommunitiesResponse.data.items;
        if (communities.length === 0) {
          return (
            <div className="text-muted py-4">{aimooT("no_communities")}</div>
          );
        }
        return (
          <div className="aimoo-community-home__cards">
            {communities.map(cv =>
              this.renderCommunityCard(cv, this.isoData.myUserInfo),
            )}
          </div>
        );
      }
      default:
        return undefined;
    }
  }

  renderCommunityCard(
    cv: CommunityView,
    myUserInfo: MyUserInfo | undefined,
  ): InfernoNode {
    const community = cv.community;
    const link = communityLink(community).link;
    const followed = cv.community_actions?.follow_state === "accepted";
    const pending = cv.community_actions?.follow_state === "pending";
    const title = community.title ?? community.name;
    const initial = title.trim().charAt(0).toUpperCase() || "?";
    const loading = this.state.followLoadingId === community.id;

    return (
      <article key={community.id} className="aimoo-community-card">
        <div className="aimoo-community-card__body">
          <Link to={link} className="aimoo-community-card__avatar-link">
            {community.icon ? (
              <PictrsImage
                src={community.icon}
                type="icon"
                nsfw={community.nsfw}
              />
            ) : (
              <div
                className={`aimoo-community-card__avatar ${avatarGradientClass(
                  community.name,
                )}`}
              >
                {initial}
              </div>
            )}
          </Link>

          <div className="aimoo-community-card__content">
            <div className="aimoo-community-card__title-row">
              <Link to={link} className="aimoo-community-card__title">
                {title}
              </Link>
              {(community as { is_official?: boolean }).is_official && (
                <span className="aimoo-community-card__badge">
                  {aimooT("official")}
                </span>
              )}
            </div>
            {community.summary && (
              <p className="aimoo-community-card__desc">{community.summary}</p>
            )}
            <div className="aimoo-community-card__meta">
              <span>
                <Icon icon="groups" classes="icon-inline me-1" />
                {aimooT("members_count", {
                  count: numToSI(community.subscribers),
                })}
              </span>
              <span>
                <Icon icon="message-square" classes="icon-inline me-1" />
                {aimooT("posts_count", { count: numToSI(community.posts) })}
              </span>
            </div>
          </div>

          {myUserInfo && (
            <button
              type="button"
              className={`aimoo-community-card__action${
                followed || pending
                  ? " aimoo-community-card__action--joined"
                  : ""
              }`}
              disabled={loading}
              onClick={() =>
                handleFollow(this, community.id as number, !followed && !pending)
              }
            >
              {loading ? (
                <Spinner />
              ) : pending ? (
                aimooT("pending")
              ) : followed ? (
                aimooT("joined")
              ) : (
                aimooT("join")
              )}
            </button>
          )}
        </div>
      </article>
    );
  }

  renderTrendingSidebar(): InfernoNode {
    return (
      <section className="aimoo-sidebar-card">
        <h2 className="aimoo-sidebar-card__title">
          {aimooT("trending_communities")}
        </h2>
        {this.state.trendingCommunitiesResponse.state === "loading" && <Spinner />}
        {this.state.trendingCommunitiesResponse.state === "success" && (
          <div className="aimoo-sidebar-card__list">
            {this.state.trendingCommunitiesResponse.data.items
              .slice(0, 5)
              .map(cv => {
                const community = cv.community;
                const title = community.title ?? community.name;
                const initial = title.trim().charAt(0).toUpperCase() || "?";
                return (
                  <Link
                    key={community.id}
                    to={communityLink(community).link}
                    className="aimoo-sidebar-item"
                  >
                    {community.icon ? (
                      <PictrsImage
                        src={community.icon}
                        type="icon"
                        nsfw={community.nsfw}
                      />
                    ) : (
                      <div
                        className={`aimoo-sidebar-item__avatar ${avatarGradientClass(
                          community.name,
                        )}`}
                      >
                        {initial}
                      </div>
                    )}
                    <div className="aimoo-sidebar-item__text">
                      <p className="aimoo-sidebar-item__name">{title}</p>
                      <p className="aimoo-sidebar-item__meta">
                        {aimooT("members_count", {
                          count: numToSI(community.subscribers),
                        })}
                      </p>
                    </div>
                  </Link>
                );
              })}
          </div>
        )}
      </section>
    );
  }

  renderMyCommunitiesSidebar(
    myUserInfo: MyUserInfo | undefined,
  ): InfernoNode {
    const follows = myUserInfo?.follows ?? [];
    return (
      <section className="aimoo-sidebar-card">
        <div className="aimoo-sidebar-card__header">
          <h2 className="aimoo-sidebar-card__title">
            {aimooT("my_communities")}
          </h2>
          <Link to="/my_communities" className="aimoo-sidebar-card__link">
            {aimooT("view_all")}
          </Link>
        </div>
        {follows.length === 0 ? (
          <p className="aimoo-sidebar-card__empty">
            {myUserInfo
              ? aimooT("not_joined_any")
              : aimooT("login_to_view_joined")}
          </p>
        ) : (
          <div className="aimoo-sidebar-card__list">
            {follows.slice(0, 8).map(cfv => {
              const community = cfv.community;
              const title = community.title ?? community.name;
              const initial = title.trim().charAt(0).toUpperCase() || "?";
              return (
                <Link
                  key={community.id}
                  to={communityLink(community).link}
                  className="aimoo-sidebar-item aimoo-sidebar-item--compact"
                >
                  {community.icon ? (
                    <PictrsImage
                      src={community.icon}
                      type="icon"
                      nsfw={community.nsfw}
                    />
                  ) : (
                    <div
                      className={`aimoo-sidebar-item__avatar aimoo-sidebar-item__avatar--sm ${avatarGradientClass(
                        community.name,
                      )}`}
                    >
                      {initial}
                    </div>
                  )}
                  <span className="aimoo-sidebar-item__name">{title}</span>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    );
  }

  updateUrl(props: Partial<CommunityHomeProps>) {
    const { listingType, sort, cursor, search_term, category_id } = {
      ...this.props,
      ...props,
    };
    const queryParams: QueryParams<CommunityHomeProps> = {
      listingType,
      sort,
      cursor,
      search_term,
      category_id:
        category_id !== undefined ? String(category_id) : undefined,
    };
    this.props.history.push(`/${getQueryString(queryParams)}`);
  }

  static fetchInitialData = async ({
    headers,
    query: { listingType, sort, cursor, search_term, category_id },
  }: InitialFetchRequest<
    CommunityHomePathProps,
    CommunityHomeProps
  >): Promise<CommunityHomeData> => {
    const client = wrapClient(
      new LemmyHttp(getHttpBaseInternal(), { headers }),
    );

    const listForm: ListCommunitiesWithCategory = {
      type_: listingType,
      sort,
      limit: communityLimit,
      page_cursor: cursor,
      search_term: search_term || undefined,
      category_id,
    };
    const trendingForm: ListCommunities = {
      type_: "local",
      sort: "hot",
      limit: 8,
    };

    const [
      listCommunitiesResponse,
      trendingCommunitiesResponse,
      listCategoriesResponse,
    ] = await Promise.all([
      client.listCommunities(listForm),
      client.listCommunities(trendingForm),
      listCommunityCategories(getHttpBaseInternal(), headers, {
        with_communities_only: true,
      }),
    ]);

    return {
      listCommunitiesResponse,
      trendingCommunitiesResponse,
      listCategoriesResponse,
    };
  };

  fetchToken?: symbol;
  async refetch(props: CommunityHomeProps) {
    const token = (this.fetchToken = Symbol());
    this.setState({
      listCommunitiesResponse: LOADING_REQUEST,
      trendingCommunitiesResponse: LOADING_REQUEST,
      listCategoriesResponse: LOADING_REQUEST,
    });

    const listForm: ListCommunitiesWithCategory = {
      type_: props.listingType,
      sort: props.sort,
      limit: communityLimit,
      page_cursor: props.cursor,
      search_term: props.search_term || undefined,
      category_id: props.category_id,
    };

    const [
      listCommunitiesResponse,
      trendingCommunitiesResponse,
      listCategoriesResponse,
    ] = await Promise.all([
      HttpService.client.listCommunities(listForm),
      HttpService.client.listCommunities({
        type_: "local",
        sort: "hot",
        limit: 8,
      }),
      browserListCommunityCategories({ with_communities_only: true }),
    ]);

    if (token === this.fetchToken) {
      this.setState({
        listCommunitiesResponse,
        trendingCommunitiesResponse,
        listCategoriesResponse,
      });
    }
  }

  findAndUpdateCommunity(res: RequestState<CommunityResponse>) {
    this.setState(s => {
      if (
        s.listCommunitiesResponse.state === "success" &&
        res.state === "success"
      ) {
        s.listCommunitiesResponse.data.items = editCommunity(
          res.data.community_view,
          s.listCommunitiesResponse.data.items,
        );
      }
      if (
        s.trendingCommunitiesResponse.state === "success" &&
        res.state === "success"
      ) {
        s.trendingCommunitiesResponse.data.items = editCommunity(
          res.data.community_view,
          s.trendingCommunitiesResponse.data.items,
        );
      }
      return s;
    });
  }
}

function handleSearchInput(i: CommunityHome, event: FormEvent<HTMLInputElement>) {
  i.setState({ searchText: event.currentTarget.value });
}

function handleSearchSubmit(
  i: CommunityHome,
  event: FormEvent<HTMLFormElement>,
) {
  event.preventDefault();
  const search_term = i.state.searchText.trim() || undefined;
  i.updateUrl({ search_term, cursor: undefined });
}

async function handleFollow(
  i: CommunityHome,
  communityId: number,
  follow: boolean,
) {
  i.setState({ followLoadingId: communityId });
  const res = await HttpService.client.followCommunity({
    community_id: communityId as any,
    follow,
  });
  i.findAndUpdateCommunity(res);
  i.setState({ followLoadingId: undefined });
}
